/**
 * OfferDraftCard — the offer lifecycle surface inside the reservation detail.
 *
 * Shows the current OfferDraft (if any) and an editor to create/update it, plus
 * the send/accept/decline lifecycle actions. ALL mutations are NON-OPTIMISTIC:
 * offers are guest-facing commitments, so every action awaits the backend and
 * the hook layer invalidates the cache on success.
 */

import {
  useAcceptOffer,
  useDeclineOffer,
  useDraftOffer,
  useOffer,
  useSendOffer,
} from '@hostesa/api-client';
import { useT } from '@hostesa/i18n';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  StatusBadge,
  Textarea,
  toast,
} from '@hostesa/ui';
import { useState } from 'react';
import type { OfferDraft, Reservation, StatusVariant } from './_deps';
import { asString, isConflict } from './_format';
import { namespace } from './i18n';

function offerStatusVariant(status: OfferDraft['status']): StatusVariant {
  switch (status) {
    case 'drafted':
      return 'neutral';
    case 'sent':
      return 'pending';
    case 'accepted':
      return 'success';
    case 'declined':
      return 'inactive';
    case 'expired':
      return 'warning';
  }
}

interface OfferDraftFormState {
  totalPrice: string;
  currency: string;
  cancellationTerms: string;
  offerValidUntil: string;
}

function initialFormState(offer: OfferDraft | undefined): OfferDraftFormState {
  return {
    totalPrice: asString(offer?.totalPrice) ?? '',
    currency: offer?.currency ?? '',
    cancellationTerms: asString(offer?.cancellationTerms) ?? '',
    offerValidUntil: offer?.offerValidUntil ?? '',
  };
}

export interface OfferDraftCardProps {
  tenantId: string;
  hotelId: string;
  reservation: Reservation;
}

export function OfferDraftCard({ tenantId, hotelId, reservation }: OfferDraftCardProps) {
  const { t } = useT(namespace);

  // A reservation does not carry its offer id; the detail surfaces an offer only
  // once one is fetched. We track the active offer id locally after a draft is
  // created so the lifecycle actions can target it.
  const [offerId, setOfferId] = useState<string>('');
  const offerQuery = useOffer(tenantId, hotelId, offerId);
  const offer = offerQuery.data;

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<OfferDraftFormState>(() => initialFormState(undefined));

  const draftOffer = useDraftOffer(tenantId, hotelId);
  const sendOffer = useSendOffer(tenantId, hotelId);
  const acceptOffer = useAcceptOffer(tenantId, hotelId);
  const declineOffer = useDeclineOffer(tenantId, hotelId);

  const busy =
    draftOffer.isPending || sendOffer.isPending || acceptOffer.isPending || declineOffer.isPending;

  function openEditor() {
    setForm(initialFormState(offer));
    setEditing(true);
  }

  async function handleSave() {
    try {
      const created = await draftOffer.mutateAsync({
        reservationId: reservation.id,
        body: {
          totalPrice: form.totalPrice || undefined,
          currency: form.currency || undefined,
          cancellationTerms: form.cancellationTerms || undefined,
          offerValidUntil: form.offerValidUntil || undefined,
        } as never,
      });
      setOfferId(created.id);
      setEditing(false);
      toast.success(t('offer.saved'));
    } catch {
      toast.error(t('offer.error'));
    }
  }

  async function runLifecycle(
    action: 'send' | 'accept' | 'decline',
    targetId: string,
  ): Promise<void> {
    const mutation =
      action === 'send' ? sendOffer : action === 'accept' ? acceptOffer : declineOffer;
    try {
      await mutation.mutateAsync({ offerId: targetId });
      toast.success(
        t(
          action === 'send'
            ? 'offer.sent_toast'
            : action === 'accept'
              ? 'offer.accepted_toast'
              : 'offer.declined_toast',
        ),
      );
    } catch (err) {
      toast.error(isConflict(err) ? t('transition.conflict') : t('offer.error'));
    }
  }

  return (
    <Card data-testid="offer-draft-card">
      <CardHeader>
        <CardTitle>{t('offer.title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {offer ? (
          <div className="flex items-center justify-between" data-testid="offer-summary">
            <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
              <dt className="text-[var(--color-stone-mid)]">{t('offer.field.totalPrice')}</dt>
              <dd className="text-[var(--color-charcoal)]">
                {asString(offer.totalPrice) ?? t('detail.notProvided')} {offer.currency}
              </dd>
              <dt className="text-[var(--color-stone-mid)]">
                {t('offer.field.cancellationTerms')}
              </dt>
              <dd className="text-[var(--color-charcoal)]">
                {asString(offer.cancellationTerms) ?? t('detail.notProvided')}
              </dd>
            </dl>
            <StatusBadge
              data-testid="offer-status-badge"
              status={offerStatusVariant(offer.status)}
              label={t(`offer.status.${offer.status}`)}
            />
          </div>
        ) : (
          <p className="text-sm text-[var(--color-stone-mid)]" data-testid="offer-none">
            {t('offer.none')}
          </p>
        )}

        {editing ? (
          <div className="flex flex-col gap-3" data-testid="offer-editor">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="offer-total-price">{t('offer.field.totalPrice')}</Label>
              <Input
                id="offer-total-price"
                data-testid="offer-total-price-input"
                value={form.totalPrice}
                placeholder={t('offer.placeholder.totalPrice')}
                onChange={(e) => setForm((f) => ({ ...f, totalPrice: e.target.value }))}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="offer-currency">{t('offer.field.currency')}</Label>
              <Input
                id="offer-currency"
                data-testid="offer-currency-input"
                value={form.currency}
                placeholder={t('offer.placeholder.currency')}
                onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="offer-cancellation-terms">{t('offer.field.cancellationTerms')}</Label>
              <Textarea
                id="offer-cancellation-terms"
                data-testid="offer-cancellation-terms-input"
                value={form.cancellationTerms}
                placeholder={t('offer.placeholder.cancellationTerms')}
                onChange={(e) => setForm((f) => ({ ...f, cancellationTerms: e.target.value }))}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="offer-valid-until">{t('offer.field.validUntil')}</Label>
              <Input
                id="offer-valid-until"
                data-testid="offer-valid-until-input"
                value={form.offerValidUntil}
                onChange={(e) => setForm((f) => ({ ...f, offerValidUntil: e.target.value }))}
              />
            </div>
            <div className="flex gap-2">
              <Button
                data-testid="offer-save-btn"
                size="sm"
                disabled={busy}
                onClick={() => void handleSave()}
              >
                {draftOffer.isPending ? t('offer.saving') : t('offer.save')}
              </Button>
              <Button
                data-testid="offer-cancel-edit-btn"
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={() => setEditing(false)}
              >
                {t('detail.close')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            <Button
              data-testid="offer-edit-btn"
              size="sm"
              variant="secondary"
              disabled={busy}
              onClick={openEditor}
            >
              {offer ? t('offer.edit') : t('offer.create')}
            </Button>
            {offer && offer.status === 'drafted' && (
              <Button
                data-testid="offer-send-btn"
                size="sm"
                disabled={busy}
                onClick={() => void runLifecycle('send', offer.id)}
              >
                {sendOffer.isPending ? t('offer.sending') : t('offer.send')}
              </Button>
            )}
            {offer && offer.status === 'sent' && (
              <>
                <Button
                  data-testid="offer-accept-btn"
                  size="sm"
                  disabled={busy}
                  onClick={() => void runLifecycle('accept', offer.id)}
                >
                  {acceptOffer.isPending ? t('offer.accepting') : t('offer.accept')}
                </Button>
                <Button
                  data-testid="offer-decline-btn"
                  size="sm"
                  variant="outline"
                  disabled={busy}
                  onClick={() => void runLifecycle('decline', offer.id)}
                >
                  {declineOffer.isPending ? t('offer.declining') : t('offer.decline')}
                </Button>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
